import m from '@bitstillery/mithril'
import {logger} from '@bitstillery/mithril'
import {classes, route_params_text, unique_id} from '@bitstillery/common/lib/utils'
import {MithrilComponent} from '@bitstillery/mithril'
import {content_props, tip} from '../tip/tip'
import {TipMemo} from '../tip/tip_memo'
import type {Instance as TippyInstance, Props as TippyProps} from 'tippy.js'

import {Icon} from '../icon/icon'
import type {IconAttrs} from '../icon/icon'
import {Spinner} from '../spinner/spinner'
import type {CmsComponentMeta} from '@bitstillery/common/cms/catalog_meta'

export const buttonMeta = {
    tier: 'inline',
    summary: 'Primary button or link-styled control.',
    when_to_use:
        'A call-to-action at the end of a `Section` or `HeroBand` body. Navigate with `link` (NOT `href`). Avoid heavy tooltips on SSR-critical paths.',
    props: "text?: string — Button label (or pass children).\nlink?: string — Navigation target — this is the href (internal route, or http(s)/mailto/tel which open in a new tab). Omit for a pure onclick button.\nonclick?: (e) => void — Click handler (e.g. a `require_login` funnel CTA).\nicon?: string — Optional leading sprite icon name.\nvariant?: 'action' | 'link' | 'toggle' | 'context' | 'icon' | 'menu' | 'cta' (default 'action') — 'action' is a filled button; 'link' is link-styled; 'cta' is a frosted white pill (drop shadow, hover-lift) for a call-to-action over a photo or dark band — pair it with a signal `type` (info/success/danger/warning) for the accent colour.\ntype?: 'brand' | 'info' | 'success' | 'warning' | 'danger' | 'surface' | 'default' (default 'default') — colour/theme.\nchrome?: boolean — place the button on a chrome surface (deep, theme-fixed). It adapts to `type`: default → translucent glass, info → light fill + chrome text, brand/other signals → the signal's vivid colour. Identical in both themes.\nsize?: 'xs' | 's' | 'd' | 'l' | 'xl' | 'xxl' | 'xxxl' (default 'd').\ntarget?: string — e.g. '_blank'.\ndisabled?: boolean\nloading?: boolean — show a spinner (use with `icon`).\nactive?: boolean — highlight as the active route.",
    example: `
    <Button link="/offers" text="Browse the stock" type="info" variant="cta" />
    `,
    variants: [
        {
            // `toggle` and `icon` render the icon alone, so a label-only base makes them an empty pill.
            base: true,
            prop: 'variant',
            jsx: `
            <Button icon="globe" text="Browse the stock" />
            `,
        },
        {
            prop: 'icon',
            jsx: `
            <Button icon="globe" link="/offers" text="Browse the stock" type="info" />
            `,
        },
        {
            prop: 'loading',
            jsx: `
            <Button icon="globe" loading text="Browse the stock" type="info" />
            `,
        },
        {
            prop: 'disabled',
            jsx: `
            <Button disabled link="/offers" text="Browse the stock" type="info" />
            `,
        },
        {
            prop: 'active',
            jsx: `
            <Button active link="/offers" text="Browse the stock" type="info" variant="menu" />
            `,
        },
        {
            // `chrome` only means anything ON a chrome surface, so the sample brings one.
            prop: 'chrome',
            label: 'chrome (on a Band)',
            jsx: `
            <Band hue={195}>
                <Button chrome link="/offers" text="Browse the stock" type="info" />
            </Band>
            `,
        },
    ],
} satisfies CmsComponentMeta

export interface ButtonAttrs {
    activated?: boolean
    /** Only a boolean sets it; any other value leaves it to the route match. */
    active?: boolean | number | null | undefined
    active_exact?: boolean
    additional_classes?: string
    /** Place the button on a chrome surface (deep, theme-fixed): it adapts to its `type` — default → glass,
     *  info → light fill, brand/other signals → the signal's vivid colour. */
    chrome?: boolean
    class_name?: string
    className?: string
    /** Tested for truthiness only. */
    disabled?: boolean | number | string | null | undefined

    id?: string
    icon?: string | undefined
    loading?: boolean | undefined
    link?: string | undefined
    /** Url to route back to when clicking an active button */
    replace_history?: boolean
    onclick?: ((e: MouseEvent, ...args: unknown[]) => unknown) | undefined
    onblur?: (e: FocusEvent) => void
    onfocus?: ((e: FocusEvent) => void) | undefined
    /** Always triggers a full page re-render */
    refresh?: boolean
    size?: IconAttrs['size']
    target?: string
    align?: string
    type?: 'brand' | 'danger' | 'surface' | 'default' | 'info' | 'success' | 'warning' | 'unset' | (string & {}) | undefined
    tip?: string | (() => unknown) | undefined
    tooltip?: string
    tip_config?: Partial<TippyProps>
    text?: string | undefined
    variant?: 'action' | 'link' | 'toggle' | 'context' | 'icon' | 'menu' | 'cta' | 'unset' | (string & {}) | undefined
    tabindex?: number | undefined
    key?: string | number
    link_options?: Record<string, unknown>
}

export class Button extends MithrilComponent<ButtonAttrs> {
    id = unique_id(8, false)
    tippy: TippyInstance | undefined
    /** Latest vnode, so tip callbacks resolve against current attrs instead of the oncreate closure. */
    current_vnode: m.Vnode<ButtonAttrs>
    /** Skips tippy when the tip has not changed; see TipMemo for why that matters. */
    private tip_memo = new TipMemo<TippyProps['content'] | undefined>()

    constructor(vnode: m.Vnode<ButtonAttrs>) {
        super()
        this.current_vnode = vnode
    }

    elements(vnode: m.Vnode<ButtonAttrs>) {
        const elements: m.ChildArray = []
        if (vnode.attrs.icon && vnode.attrs.loading) {
            elements.push(m(Spinner))
        }
        if (vnode.attrs.icon && !vnode.attrs.loading) {
            elements.push(<Icon size={vnode.attrs.size} name={vnode.attrs.icon} type={vnode.attrs.type} />)
        }
        if (vnode.attrs.text && vnode.attrs.variant !== 'toggle') {
            elements.push(m('span', {className: 'c-button-text'}, vnode.attrs.text))
        }

        if (vnode.children) {
            if (Array.isArray(vnode.children)) {
                vnode.children.forEach((child) => elements.push(child))
            } else {
                elements.push(vnode.children)
            }
        }
        return elements
    }

    override onremove(vnode: m.Vnode<ButtonAttrs>) {
        if ((vnode.attrs.tip ?? vnode.attrs.tooltip) && this.tippy?.destroy) {
            this.tippy.destroy()
        }
    }

    override oncreate(vnode: m.Vnode<ButtonAttrs>) {
        if (vnode.attrs.tip ?? vnode.attrs.tooltip) {
            let selector: Element | null
            if (vnode.attrs.variant === 'link') {
                selector = document.querySelector(`#tip-${this.id}`)
            } else {
                selector = document.querySelector(`#c-button-${this.id}`)
            }

            const tip_config: Partial<TippyProps> = {
                allowHTML: true,
                animation: 'scale',
                appendTo: document.body,
                ...content_props(this.update_tip(vnode)),
                delay: 500,
                inertia: true,
                interactive: true,
                onTrigger: (instance: TippyInstance) => {
                    instance.setProps(content_props(this.update_tip(this.current_vnode)))
                },
                touch: 'hold',
                zIndex: 10_000_000_000_00_000,
            }

            if (vnode.attrs.tip_config) {
                Object.assign(tip_config, vnode.attrs.tip_config)
            }
            this.tip_memo.prime(vnode.attrs.tip ?? vnode.attrs.tooltip, tip_config.content)
            // Without its element (a caller's `id` renames it) tippy would answer an empty instance array.
            this.tippy = selector ? tip(selector, tip_config) : undefined
        }
    }

    override onupdate(vnode: m.Vnode<ButtonAttrs>) {
        this.current_vnode = vnode
        if (!this.tippy) {
            return
        }
        const next = this.tip_memo.next(vnode.attrs.tip ?? vnode.attrs.tooltip, () => this.update_tip(vnode))
        if (next) {
            this.tippy.setProps(content_props(next.content))
        }
    }

    update_tip(vnode: m.Vnode<ButtonAttrs>) {
        const t = vnode.attrs.tip ?? vnode.attrs.tooltip
        if (typeof t === 'function') {
            const content = t()
            return typeof content === 'string' ? content : undefined
        }
        return t
    }

    view(vnode: m.Vnode<ButtonAttrs>) {
        const attrs: {
            className: string
            href?: string
            id: string
            key?: string | number
            onblur?: (e: FocusEvent) => void
            onclick: (e: MouseEvent, ...args: unknown[]) => Promise<void>
            onfocus?: (e: FocusEvent) => void
            tabindex?: number | undefined
            target?: string
        } = {
            className: classes(
                'c-button',
                vnode.attrs.className ?? vnode.attrs.class_name ?? vnode.attrs.additional_classes,
                `type-${vnode.attrs.type ? vnode.attrs.type : 'default'}`,
                `size-${vnode.attrs.size ?? 'd'}`,
                `variant-${vnode.attrs.variant ? vnode.attrs.variant : 'action'}`,
                {
                    active: (() => {
                        if (typeof vnode.attrs.active === 'boolean') {
                            return vnode.attrs.active
                        }
                        if (typeof vnode.attrs.activated === 'boolean') {
                            return vnode.attrs.activated
                        }
                        if (vnode.attrs.link) {
                            if (vnode.attrs.active_exact) {
                                return m.route.get() === vnode.attrs.link
                            }
                            return m.route.get().startsWith(vnode.attrs.link ?? '')
                        }
                        return undefined
                    })(),
                    chrome: vnode.attrs.chrome,
                    disabled: vnode.attrs.disabled,
                },
            ),
            id: `c-button-${vnode.attrs.id ?? this.id}`,
            onclick: async (e: MouseEvent, ...args: unknown[]) => {
                if (vnode.attrs.disabled) {
                    e.preventDefault()
                }

                if (vnode.attrs.link && !vnode.attrs.disabled && vnode.attrs.target !== '_blank') {
                    if (vnode.attrs.link.startsWith('tel:') || vnode.attrs.link.startsWith('mailto:')) {
                        window.open(vnode.attrs.link)
                        return
                    }
                    e.preventDefault()
                    e.stopPropagation()
                    const urlpath = m.parsePathname(m.route.get())
                    const url_params = route_params_text(urlpath.params)
                    const {path: link_path} = m.parsePathname(vnode.attrs.link ?? '')
                    const ctrl_down = e.ctrlKey

                    if (vnode.attrs.refresh) {
                        logger.debug(`[button] refresh route: ${vnode.attrs.link}`)
                        await m.route.set(vnode.attrs.link, undefined, {remount: true})
                    } else {
                        if (vnode.attrs.active) {
                            if (link_path === urlpath.path) {
                                logger.debug(`[button] same route; refresh: ${vnode.attrs.link}`)
                                await m.route.set(vnode.attrs.link, undefined, {remount: true})
                            } else {
                                logger.debug(`[button] to new route: ${vnode.attrs.link}`)
                                if (ctrl_down) {
                                    window.open(`${vnode.attrs.link}`)
                                } else {
                                    void m.route.set(vnode.attrs.link)
                                }
                            }
                        } else {
                            if (vnode.attrs.replace_history) {
                                // Prevents poluting the history state.
                                void m.route.set(vnode.attrs.link, url_params, {replace: vnode.attrs.replace_history})
                            } else {
                                if (ctrl_down || vnode.attrs.target === '_blank') {
                                    window.open(`${vnode.attrs.link}`)
                                } else {
                                    void m.route.set(vnode.attrs.link)
                                }
                            }
                        }
                    }
                }

                if (vnode.attrs.onclick && !vnode.attrs.disabled) {
                    vnode.attrs.onclick(e, ...args)
                }
            },
            tabindex: vnode.attrs.tabindex,
        }

        if (vnode.attrs.onblur) {
            attrs.onblur = vnode.attrs.onblur
        }
        if (vnode.attrs.onfocus) {
            attrs.onfocus = vnode.attrs.onfocus
        }

        if (vnode.attrs.key) {
            attrs.key = vnode.attrs.key
        }
        if (vnode.attrs.link) {
            if (
                vnode.attrs.link.startsWith('mailto:') ||
                vnode.attrs.link.startsWith('tel:') ||
                vnode.attrs.link.startsWith('http')
            ) {
                attrs.href = vnode.attrs.link
                attrs.target = '_blank'
            } else {
                if (vnode.attrs.target) {
                    attrs.target = vnode.attrs.target
                }
                attrs.href = vnode.attrs.link
            }
        }
        const elements = [m(vnode.attrs.link ? 'a' : 'button', attrs, this.elements(vnode))]

        if (vnode.attrs.variant === 'link' && (vnode.attrs.tip ?? vnode.attrs.tooltip)) {
            elements.push(<Icon className='tip' id={`tip-${this.id}`} name='info' size='s' type='info' />)
        }

        return elements
    }
}
